import { useAuth0 } from "@auth0/auth0-react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { ChatActions, ReceivedWebSocketMessage, WebSocketMessage, WebSocketServices } from "@utils/constants";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useDebounce } from "use-debounce";

import { Headline, SkeletonLoader, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiChatMessageRoleChoices,
  ApiChatStatusChoices,
  ChatStatusEnums,
  ChatType,
  ChatTypeEnums,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { ChatCard } from "@pages/FronteraClarity/components/ChatCard";
import { ChatStatus } from "@pages/FronteraClarity/components/ChatStatus";
import { useExpertChatMembers } from "@pages/FronteraClarity/hooks/useExpertChatMembers";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";
import { useAdminData } from "@providers/AdminDataProvider";

enum ChatTabType {
  Active = "active",
  Waiting = "waiting",
  Archived = "archived",
}

interface ExpertChatSidePanelProps {
  selectedChat: ChatType | undefined;
  setSelectedChat: React.Dispatch<React.SetStateAction<ChatType | undefined>>;
}

export const ExpertChatSidePanel: React.FC<ExpertChatSidePanelProps> = ({ selectedChat, setSelectedChat }) => {
  const { roles } = useAdminData();
  const { user, getAccessTokenSilently } = useAuth0();
  const { isMobile } = useBreakpoint();
  const [accessToken, setAccessToken] = useState<string>();

  // TODO: using invalidateQueries should be temporary and we should rebuild the chat UI around the WS updating the chats state directly to avoid a bunch of refetches
  const queryClient = useQueryClient();

  const { chatId } = useParams<{ chatId?: string; chatType?: ChatTabType }>();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<ChatTabType>(ChatTabType.Active);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const showOpenTab = selectedTab === ChatTabType.Active || selectedTab === ChatTabType.Waiting;

  const open = useExpertChatMembers({
    filter: { searchTerm: debouncedSearchTerm, types: [ChatTypeEnums.Expert], status: ChatStatusEnums.Open },
    enabled: showOpenTab || !!chatId,
    fetchAllPages: true,
  });
  const finished = useExpertChatMembers({
    filter: { searchTerm: debouncedSearchTerm, types: [ChatTypeEnums.Expert], status: ChatStatusEnums.Finished },
    enabled: selectedTab === ChatTabType.Archived || !!chatId,
  });

  if (open.error || finished.error) {
    notifyError("Failed to load chats");
    return;
  }

  const openChats = open.chats;
  const finishedChats = finished.chats;
  const allChats = [...openChats, ...finishedChats];

  const chatsLoading = selectedTab === ChatTabType.Archived ? finished.isLoading : open.isLoading;

  // A chat is "waiting on caregiver" only when the provider has sent a real (non-system) message.
  // System-role messages are automated — they don't count as a provider response.
  const isChatWaiting = (chat: ChatType) => {
    const lastFromProvider = chat?.lastMessage?.user?.auth0id === user?.sub;
    const lastIsSystem = chat?.lastMessage?.role === ApiChatMessageRoleChoices.System;
    return lastFromProvider && !lastIsSystem;
  };

  const activeCount = openChats.filter((chat) => !isChatWaiting(chat)).length;
  const waitingCount = openChats.filter((chat) => isChatWaiting(chat)).length;

  const getDisplayedChats = () => {
    if (selectedTab === ChatTabType.Active) return openChats.filter((chat) => !isChatWaiting(chat));
    if (selectedTab === ChatTabType.Waiting) return openChats.filter((chat) => isChatWaiting(chat));
    return finishedChats;
  };
  const displayedChats = getDisplayedChats();

  const activeList = selectedTab === ChatTabType.Archived ? finished : open;
  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom && activeList.hasNextPage && !activeList.isFetchingMore) {
      activeList.fetchNextPage();
    }
  };

  // select chat based on url
  useEffect(() => {
    if (!chatId) {
      setSelectedChat(undefined);
      return;
    }

    const match = allChats.find((chat) => chat?.id === chatId);
    if (!match) {
      return;
    }

    if (match.status === ApiChatStatusChoices.Finished) {
      setSelectedTab(ChatTabType.Archived);
    } else {
      setSelectedTab(isChatWaiting(match) ? ChatTabType.Waiting : ChatTabType.Active);
    }
    setSelectedChat(match);
  }, [chatId, openChats, finishedChats, user?.sub]);

  const handleChatSelect = (chat: ChatType) => {
    setSelectedChat(chat);
    navigate(`/frontera-clarity/expert-chat/${chat.id}`, { replace: true });
  };

  const onArchive = () => {
    setSelectedChat(undefined);
    navigate("/frontera-clarity/expert-chat", { replace: true });
  };

  // connect to websocket; this is used for updated chats panel when new updates come through
  const socketUrl = accessToken ? import.meta.env.VITE_WEBSOCKET_DOMAIN : null;

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (mounted) {
          setAccessToken(token);
        }
      } catch (error) {
        console.error("Failed to get access token:", error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently]);

  const { sendJsonMessage, readyState } = useWebSocket<ReceivedWebSocketMessage>(socketUrl, {
    share: true,
    onError: (error) => {
      console.error("WebSocket error:", error);
    },
    onMessage: (event) => {
      if (!accessToken) {
        return;
      }

      const wsMessage = JSON.parse(event.data) as ReceivedWebSocketMessage;

      if (wsMessage.payload.status !== 200) {
        notifyError(wsMessage.payload.message as string);
        return;
      }

      if (wsMessage.service === WebSocketServices.CHAT) {
        switch (wsMessage.action) {
          case ChatActions.UPDATE:
            queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
            return;
        }
      }
    },
    shouldReconnect: () => !!accessToken,
  });

  // subscribe to updates when WS connects
  useEffect(() => {
    if (accessToken && readyState === ReadyState.OPEN) {
      const subscribePayload: WebSocketMessage = {
        service: WebSocketServices.CHAT,
        action: ChatActions.UPDATE,
        timestamp: new Date(),
        accessToken: accessToken,
        payload: {},
      };
      sendJsonMessage(subscribePayload);
    }
  }, [accessToken, readyState, sendJsonMessage]);

  return (
    <div className={clsx("flex flex-col bg-white", isMobile ? "h-full" : "rounded-lg border min-w-[325px] w-[25%]")}>
      {roles.includes("frontera-clarity-provider") && <ChatStatus />}
      {/* Chat Tabs */}
      <div className="flex flex-row items-center justify-between border rounded-md m-4 text-sm">
        <div
          className={clsx(
            "flex flex-row flex-1 items-center justify-center gap-2 border-r text-center cursor-pointer p-1 rounded-l-md",
            selectedTab == ChatTabType.Active ? "bg-rio-600 text-white" : "bg-limestonev2-200",
          )}
          onClick={() => {
            setSelectedTab(ChatTabType.Active);
          }}
        >
          Active
          <div className="bg-white text-limestone-700 rounded-md px-1 leading-[18px]">{activeCount}</div>
        </div>
        <div
          className={clsx(
            "flex flex-row flex-1 items-center justify-center gap-2 text-center cursor-pointer p-1",
            selectedTab == ChatTabType.Waiting ? "bg-[#24727F] text-white" : "bg-limestonev2-200",
          )}
          onClick={() => {
            setSelectedTab(ChatTabType.Waiting);
          }}
        >
          Waiting
          <div className="bg-white text-limestone-700 rounded-md px-1 leading-[18px]">{waitingCount}</div>
        </div>
        <div
          className={clsx(
            "flex-1 border-l text-center cursor-pointer p-1 rounded-r-md",
            selectedTab == ChatTabType.Archived ? "bg-rio-600 text-white" : "bg-limestonev2-200",
          )}
          onClick={() => {
            setSelectedTab(ChatTabType.Archived);
          }}
        >
          Archived
        </div>
      </div>

      {/* Searching */}
      <div className="flex flex-row items-center justify-between px-4 pb-4">
        <input
          className="flex-1 text-text-secondary! rounded-md  border-limestone-200 text-sm focus:border-limestone-300 focus:ring-limestone-300"
          placeholder="Search conversations"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Chats List */}
      <div className="flex flex-col gap-2 p-2 overflow-y-auto no-scrollbar h-full border-t" onScroll={handleListScroll}>
        {!displayedChats.length && !chatsLoading && (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-limestone-200 mx-auto mb-4" />
            <Headline size="medium" className="text-xl text-text-primary mb-2">
              No chats found
            </Headline>
            {!searchTerm && <Small className="text-text-secondary">You don't have any assigned chats yet.</Small>}
          </div>
        )}

        {chatsLoading ? (
          <SkeletonLoader loading={true} loadingClassName="justify-center min-h-[100px] min-w-[340px]" />
        ) : (
          <>
            {displayedChats.map(
              (chat) =>
                chat && (
                  <div
                    key={chat.id}
                    onClick={() => {
                      handleChatSelect(chat as ChatType);
                    }}
                  >
                    <ChatCard
                      key={chat.id}
                      chat={chat as ChatType}
                      isSelected={selectedChat?.id === chat.id}
                      onArchive={onArchive}
                    />
                  </div>
                ),
            )}
            {activeList.isFetchingMore && (
              <SkeletonLoader loading={true} loadingClassName="justify-center min-h-[60px] min-w-[340px]" />
            )}
          </>
        )}
      </div>
    </div>
  );
};
